import { useState, useEffect, useRef } from 'react';
import { User } from '../models/User';
import { getAllPosts, addPost, updatePost, deletePost } from '../data/contentDatabase';
import { addOrUpdateTimelineReaction, removeTimelineReaction, getReactionsForPost } from '../data/timelineReactionsDatabase';
import { TimelineReaction } from '../models/TimelineReaction';
import { useAuth } from './useAuth';
import { TimelinePost } from '../models/Content';
import { Timestamp } from 'firebase/firestore';
import { Attachment } from '../models/Content';

interface TimelinePostFormData {
  title: string;
  content: string;
  timelinePostCategory: 'Update' | 'Newsletter' | 'Announcement';
  attachments?: Attachment[];
}

const defaultFormData: TimelinePostFormData = {
  title: '',
  content: '',
  timelinePostCategory: 'Update',
  attachments: [],
};

// Global cache for timeline data to persist across navigation
const timelineCache = {
  posts: [] as TimelinePost[],
  reactions: [] as TimelineReaction[],
  lastFetch: 0,
  isInitialized: false,
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useTimelinePosts = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<TimelinePost[]>(timelineCache.posts);
  const [isLoading, setIsLoading] = useState(!timelineCache.isInitialized);
  const [error, setError] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [reactions, setReactions] = useState<TimelineReaction[]>(timelineCache.reactions);
  const mountedRef = useRef(true);

  // Check if cache is still valid
  const isCacheValid = () => {
    return timelineCache.isInitialized && 
           (Date.now() - timelineCache.lastFetch) < CACHE_DURATION;
  };

  // Fetch reactions for all posts
  const fetchReactions = async (postIds: string[]) => {
    if (postIds.length === 0) return;
    
    try {
      // Fetch all reactions in parallel instead of sequentially
      const reactionPromises = postIds.map(postId => getReactionsForPost(postId));
      const allReactionsArrays = await Promise.all(reactionPromises);
      
      // Flatten the arrays into a single array
      const allReactions: TimelineReaction[] = allReactionsArrays.flat();
      
      if (mountedRef.current) {
        setReactions(allReactions);
        timelineCache.reactions = allReactions;
      }
    } catch (error) {
      console.error('Failed to fetch reactions:', error);
    }
  };

  const reloadPosts = async (force = false) => {
    if (!user) return;
    
    // Use cache if valid and not forced
    if (!force && isCacheValid()) {
      setItems(timelineCache.posts);
      setReactions(timelineCache.reactions);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Fetch posts and reactions in parallel
      const [posts] = await Promise.all([
        getAllPosts('timeline'),
        // We'll fetch reactions separately to avoid blocking posts display
      ]);
      
      if (mountedRef.current) {
        setItems(posts as TimelinePost[]);
        timelineCache.posts = posts as TimelinePost[];
        timelineCache.lastFetch = Date.now();
        timelineCache.isInitialized = true;
        
        // Fetch reactions for all posts in parallel
        const postIds = posts.map(p => p.id).filter((id): id is string => id !== undefined);
        if (postIds.length > 0) {
          // Don't await this - let it load in the background
          fetchReactions(postIds);
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load posts');
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent, directFormData?: TimelinePostFormData) => {
    e.preventDefault();
    if (!user) return;
    
    // Use direct form data if provided, otherwise use state
    const dataToUse = directFormData || formData;
    
    try {
      const post: TimelinePost = {
        title: dataToUse.title,
        content: dataToUse.content,
        timelinePostCategory: dataToUse.timelinePostCategory,
        authorId: user.id,
        created_at: Timestamp.now(),
        category: 'timeline',
        attachments: dataToUse.attachments || [],
      };
      
      // Create a temporary ID for optimistic update
      const tempId = `temp_${Date.now()}`;
      const optimisticPost = { ...post, id: tempId };
      
      // Optimistically add the post to the UI and cache
      const newItems = [optimisticPost, ...items];
      setItems(newItems);
      timelineCache.posts = newItems;
      
      // Save to database and get the saved post with real ID
      const savedPost = await addPost(post);
      
      // Replace optimistic post with real one using the newItems array, not the old items
      const updatedItems = newItems.map(p => p.id === tempId ? savedPost : p);
      setItems(updatedItems);
      timelineCache.posts = updatedItems;
      
      setFormData(defaultFormData);
      setShowNewForm(false);
    } catch (err) {
      // Remove optimistic post on error
      const revertedItems = items.filter(p => !p.id?.startsWith('temp_'));
      setItems(revertedItems);
      timelineCache.posts = revertedItems;
      setError(err instanceof Error ? err.message : 'Failed to create post');
    }
  };

  const handleEdit = async (id: string, updates: Partial<TimelinePost>) => {
    if (!user) return;
    
    const originalPost = items.find(p => p.id === id);
    if (!originalPost) return;
    
    try {
      // Optimistically update the post in the UI and cache
      const updatedItems = items.map(p => p.id === id ? { ...p, ...updates } : p);
      setItems(updatedItems);
      timelineCache.posts = updatedItems;

      const updatedPost = {
        ...originalPost,
        ...updates,
      };
      await updatePost(updatedPost);
      setEditingId(null);
      setFormData(defaultFormData);
    } catch (err) {
      // Revert optimistic update on error
      const revertedItems = items.map(p => p.id === id ? originalPost : p);
      setItems(revertedItems);
      timelineCache.posts = revertedItems;
      setError(err instanceof Error ? err.message : 'Failed to update post');
    }
  };

  const handleDelete = async (id: string) => {
    if (!user) return;
    
    const postToDelete = items.find(p => p.id === id);
    if (!postToDelete) return;
    
    try {
      // Optimistically remove the post from the UI and cache
      const filteredItems = items.filter(p => p.id !== id);
      setItems(filteredItems);
      timelineCache.posts = filteredItems;
      
      await deletePost(postToDelete);
    } catch (err) {
      // Restore post on error
      const restoredItems = [postToDelete, ...items];
      setItems(restoredItems);
      timelineCache.posts = restoredItems;
      setError(err instanceof Error ? err.message : 'Failed to delete post');
    }
  };

  // Add or update a reaction with optimistic update
  const handleAddReaction = async (postId: string, reactionType: 'like' | 'love' | 'celebrate' | 'insightful', user: User) => {
    if (!user) return;
    
    // Create optimistic reaction
    const optimisticReaction: TimelineReaction = {
      id: `${postId}_${user.id}`,
      postId,
      userId: user.id,
      reactionType,
      created_at: Timestamp.now(),
    };
    
    // Optimistically update the UI and cache
    const newReactions = reactions.filter(r => !(r.postId === postId && r.userId === user.id));
    newReactions.push(optimisticReaction);
    setReactions(newReactions);
    timelineCache.reactions = newReactions;
    
    try {
      await addOrUpdateTimelineReaction(postId, user.id, reactionType);
    } catch (error) {
      // Revert optimistic update on error
      const revertedReactions = reactions.filter(r => !(r.postId === postId && r.userId === user.id));
      setReactions(revertedReactions);
      timelineCache.reactions = revertedReactions;
      console.error('Failed to add reaction:', error);
    }
  };

  // Remove a reaction with optimistic update
  const handleRemoveReaction = async (postId: string, user: User) => {
    if (!user) return;
    
    // Store the reaction to restore on error
    const existingReaction = reactions.find(r => r.postId === postId && r.userId === user.id);
    
    // Optimistically remove from UI and cache
    const filteredReactions = reactions.filter(r => !(r.postId === postId && r.userId === user.id));
    setReactions(filteredReactions);
    timelineCache.reactions = filteredReactions;
    
    try {
      await removeTimelineReaction(postId, user.id);
    } catch (error) {
      // Restore reaction on error
      if (existingReaction) {
        const restoredReactions = [...reactions, existingReaction];
        setReactions(restoredReactions);
        timelineCache.reactions = restoredReactions;
      }
      console.error('Failed to remove reaction:', error);
    }
  };

  // Get reactions for a post
  const getReactionsForTimelinePost = (postId: string) => {
    return reactions.filter(r => r.postId === postId);
  };

  // Create input change handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Load posts only if not cached or on first mount
  useEffect(() => {
    if (user?.id) {
      reloadPosts();
    }
  }, [user?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    items,
    isLoading,
    error,
    showNewForm,
    setShowNewForm,
    editingId,
    setEditingId,
    formData,
    setFormData,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleInputChange,
    reloadPosts,
    getReactionsForTimelinePost,
    handleAddReaction,
    handleRemoveReaction,
  };
}; 